import { baseApiUrl } from 'src/config';
import { getAccessToken } from 'src/routes/AuthContext';

/**
 * JOTFORM-DISCONNECT(BRK-1925): submits a native screening to document-service.
 *
 * Backend contract:
 *   POST {baseApiUrl}/document/form/screening/{formId}?webhookUuid={webhookUuid}
 *
 * The body is the raw ReForm data from ScreeningForm, keyed by section id
 * ({ basicDetails, screeningQuestions, ... }). The webhook (entityType SCREENING, minted via
 * pullUuid) carries the patient/screening context; document-service renders the PDF via
 * Printimus Prime and persists through provider-service — no Jotform anywhere.
 *
 * Follows the same fetch/header convention as submitAddendumNote.ts — the documentService/*
 * layer does not use the axios interceptor, so Authorization / x-tenant-id / Facility_id /
 * X-Forwarded-For are attached manually here.
 *
 * @param realm        tenant id (x-tenant-id header)
 * @param formId       the facility's SCREENING template form id (document.formId from
 *                     get-screening-template)
 * @param webhookUuid  SCREENING webhook uuid, sent as the webhookUuid query param
 * @param facilityId   facility id (Facility_id header)
 * @param payload      the ReForm submission data, keyed by section id
 */
export const submitScreening = async (
  realm: string,
  formId: string,
  webhookUuid: string,
  facilityId: string,
  payload: Record<string, unknown>
): Promise<{ error: boolean | string }> => {
  const token = await getAccessToken();
  const apiUrl = `${baseApiUrl}/document/form/screening/${formId}?webhookUuid=${webhookUuid}`;

  return fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-tenant-id': realm,
      Facility_id: facilityId,
      // Session IP for parity with the other document submits.
      'X-Forwarded-For': localStorage.getItem('ip-address') ?? '',
    },
    body: JSON.stringify(payload),
  })
    .then(async (response) => {
      if (response.status === 200) {
        return { error: false };
      }

      let errorMessage = 'Something went wrong';
      try {
        const errorResponse = await response.json();
        errorMessage = errorResponse.message || errorResponse.error || errorMessage;
      } catch (e) {
        errorMessage = response.statusText || `Request failed with status ${response.status}`;
      }

      return { error: errorMessage };
    })
    .catch((error) => {
      return { error: `Error submitting screening: ${error.message || error}` };
    });
};
